/* ============================================================================
   Tower search (TPS) v2.5.2 — 24 Oct 2026 (fast search no longer gets stuck in fallback mode)
   - Understands brands, styles and gender in a query and routes it to Shopify
     search with the matching Search & Discovery filters applied
     ("docs mens chelsea" -> q=chelsea + Brand: Dr. Martens + Gender: Mens).
   - Instant-search panel extras: empty-state (trending / recent / brands /
     popular products), keyboard navigation, rotating placeholder.
   - Results page extras: gender quick chips, load more, zero-result rescue.
   Config comes from snippets/tower-search-data.liquid (theme settings).
   Kept as a separate asset so Prestige updates never touch it.
   ========================================================================= */
(() => {
  'use strict';

  const dataEl = document.getElementById('tps-data');
  if (!dataEl) return;

  let C;
  try {
    C = JSON.parse(dataEl.textContent);
  } catch (e) {
    return;
  }
  if (!C || !C.enabled) return;

  document.documentElement.classList.add('tps-js');

  const SEARCH_PATH = new URL(C.searchUrl || '/search', location.origin).pathname;
  const ROOT_URL = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || C.rootUrl || '/').replace(/\/?$/, '/');
  const RECENT_KEY = 'tps:recent';
  const MAX_PHRASE = 4;
  const STOPWORDS = new Set(['for', 'in', 'the', 'and', 'with', 'of', 'by', 'a', 'an', 'on', 'to', 'shop', 'buy']);

  /* ---------------------------------------------------------------- utils */

  const norm = (s) =>
    (s || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/ø/g, 'o')
      .replace(/&/g, ' and ')
      .replace(/['’`.]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');

  const singular = (w) => (w.length > 3 && w.endsWith('s') && !w.endsWith('ss') ? w.slice(0, -1) : w);

  const lines = (txt) =>
    (txt || '')
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);

  const track = (name, detail) => {
    try {
      (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, detail));
    } catch (e) {}
    try {
      window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish(name, detail);
    } catch (e) {}
    try {
      if (typeof insights !== 'undefined') insights.forward(name, detail);
    } catch (e) {}
  };

  const store = {
    get(key, fallback) {
      try {
        const v = window.localStorage.getItem(key);
        return v ? JSON.parse(v) : fallback;
      } catch (e) {
        return fallback;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch (e) {}
    }
  };

  const escapeHtml = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const sectionIdOf = (el) => {
    const section = el && el.closest('.shopify-section');
    return section ? section.id.replace('shopify-section-', '') : null;
  };

  /* ------------------------------------------------------- dictionaries */

  // Brands: every store vendor by its own name (spaced + joined), plus aliases.
  // A key can point at several vendors: casing duplicates ("Fitflop" / "FitFlop") and brand families
  // that share a name ("skechers" -> Skechers + Skechers Performance, "helly hansen" -> Sport + Workwear).
  const brandMap = new Map(); // key -> { vendors: [], alias }
  const vendorsByNorm = new Map(); // norm(vendor) -> [vendor, ...]
  const addBrand = (key, vendors, alias) => {
    if (!key) return;
    const current = brandMap.get(key);
    if (!current || (alias && !current.alias)) {
      brandMap.set(key, { vendors: [...vendors], alias });
    } else if (current.alias === alias) {
      vendors.forEach((v) => !current.vendors.includes(v) && current.vendors.push(v));
    }
  };

  (C.vendors || []).forEach((v) => {
    const n = norm(v);
    if (!n) return;
    if (!vendorsByNorm.has(n)) vendorsByNorm.set(n, []);
    vendorsByNorm.get(n).push(v);
  });

  vendorsByNorm.forEach((vendors, n) => {
    addBrand(n, vendors, false);
    addBrand(n.replace(/ /g, ''), vendors, false);
    if (n.includes(' and ')) addBrand(n.replace(/ and /g, ' '), vendors, false);
  });

  // Families: a leading word-group shared by 2+ different vendors, and at least one of them is that exact name
  // or the group is 2+ words. Single first words owned by one vendor ("desert", "hard", "jack") never route.
  const families = new Map();
  vendorsByNorm.forEach((vendors, n) => {
    const words = n.split(' ');
    for (let k = 1; k < words.length; k++) {
      const prefix = words.slice(0, k).join(' ');
      if (prefix.length < 3) continue;
      if (!families.has(prefix)) families.set(prefix, new Set());
      vendors.forEach((v) => families.get(prefix).add(v));
    }
  });
  families.forEach((set, prefix) => {
    const own = vendorsByNorm.get(prefix) || [];
    own.forEach((v) => set.add(v));
    if (set.size >= 2 && (own.length || prefix.includes(' '))) {
      addBrand(prefix, [...set], false);
      addBrand(prefix.replace(/ /g, ''), [...set], false);
    }
  });

  lines(C.brandAliases).forEach((line) => {
    const [left, right] = line.split('=');
    if (!left || !right) return;
    const target = vendorsByNorm.get(norm(right));
    if (!target) return; // only alias to brands the store actually carries
    const vendors = (brandMap.get(norm(right)) || { vendors: target }).vendors;
    left.split(',').forEach((a) => {
      const n = norm(a);
      if (n) {
        addBrand(n, vendors, true);
        addBrand(n.replace(/ /g, ''), vendors, true);
      }
    });
  });

  // Styles: only the curated words route to a product type.
  const typeMap = new Map();
  const typeByNorm = new Map((C.types || []).map((t) => [norm(t), t]));
  lines(C.typeSynonyms).forEach((line) => {
    const [left, right] = line.split('=');
    if (!left || !right) return;
    const types = right
      .split(';')
      .map((t) => typeByNorm.get(norm(t)) || (typeByNorm.size ? null : t.trim()))
      .filter(Boolean);
    if (!types.length) return;
    left.split(',').forEach((w) => {
      const n = norm(w);
      if (n) typeMap.set(n, types);
    });
  });

  // Gender groups: "Label : words : value / value"
  const genderGroups = [];
  const genderMap = new Map();
  lines(C.genderGroups).forEach((line) => {
    const parts = line.split(':');
    if (parts.length < 3) return;
    const group = {
      label: parts[0].trim(),
      values: parts.slice(2).join(':').split('/').map((v) => v.trim()).filter(Boolean)
    };
    if (!group.values.length) return;
    genderGroups.push(group);
    parts[1].split(',').forEach((w) => {
      const n = norm(w);
      if (n) genderMap.set(n, group);
    });
    genderMap.set(norm(group.label), group);
  });

  /* ---------------------------------------------------------- the parser */

  const lookup = (phrase, wholeQuery) => {
    const joined = phrase.replace(/ /g, '');
    const words = phrase.split(' ');
    const sing = words.slice(0, -1).concat(singular(words[words.length - 1])).join(' ');

    // Brand first (longest phrase wins because we scan long -> short)
    for (const key of [phrase, joined, sing]) {
      const b = brandMap.get(key);
      // 1-2 letter vendor names ("On") only count when they are the whole query; aliases ("nb") always count
      if (b && (b.alias || key.length > 2 || wholeQuery)) return { kind: 'brand', value: b.vendors };
    }
    for (const key of [phrase, sing]) {
      if (typeMap.has(key)) return { kind: 'type', value: typeMap.get(key) };
    }
    for (const key of [phrase, sing]) {
      if (genderMap.has(key)) return { kind: 'gender', value: genderMap.get(key) };
    }
    return null;
  };

  const parse = (query) => {
    const tokens = norm(query).split(' ').filter(Boolean);
    const result = { brands: [], types: [], genders: [], rest: [] };

    for (let i = 0; i < tokens.length; ) {
      let hit = null;
      let used = 1;
      for (let n = Math.min(MAX_PHRASE, tokens.length - i); n >= 1; n--) {
        const phrase = tokens.slice(i, i + n).join(' ');
        const found = lookup(phrase, n === tokens.length);
        if (found) {
          hit = found;
          used = n;
          break;
        }
      }

      if (!hit) {
        result.rest.push(tokens[i]);
      } else if (hit.kind === 'brand') {
        hit.value.forEach((v) => !result.brands.includes(v) && result.brands.push(v));
      } else if (hit.kind === 'type') {
        hit.value.forEach((t) => !result.types.includes(t) && result.types.push(t));
      } else if (hit.kind === 'gender') {
        if (!result.genders.includes(hit.value)) result.genders.push(hit.value);
      }
      i += used;
    }

    const understood = result.brands.length + result.types.length + result.genders.length > 0;
    if (understood) {
      result.rest = result.rest.filter((w) => !STOPWORDS.has(w));
    }
    result.understood = understood;
    return result;
  };

  const buildUrl = (query, parsed) => {
    const url = new URL(C.searchUrl || '/search', location.origin);
    if (!C.routing || !parsed.understood) {
      url.searchParams.set('q', query);
      return url;
    }

    const rest = parsed.rest.join(' ');
    const q = rest || (parsed.brands.length === 1 ? parsed.brands[0] : '*');
    url.searchParams.set('q', q);
    url.searchParams.set('type', 'product');
    parsed.brands.forEach((b) => url.searchParams.append('filter.p.vendor', b));
    parsed.types.forEach((t) => url.searchParams.append('filter.p.product_type', t));
    const genderValues = [...new Set(parsed.genders.flatMap((g) => g.values))];
    genderValues.forEach((g) => url.searchParams.append(C.genderParam, g));
    url.searchParams.set('oq', query);
    return url;
  };

  /* ------------------------------------------------------ recent searches */

  const recent = {
    all: () => store.get(RECENT_KEY, []),
    add(q) {
      const clean = q.trim();
      if (!clean || clean === '*') return;
      const list = recent.all().filter((x) => x.toLowerCase() !== clean.toLowerCase());
      list.unshift(clean);
      store.set(RECENT_KEY, list.slice(0, 6));
    },
    clear: () => store.set(RECENT_KEY, [])
  };

  /* -------------------------------------------------------------- routing */

  const go = (query, source) => {
    const q = (query || '').trim();
    if (!q) return;
    recent.add(q);
    const parsed = parse(q);
    const url = buildUrl(q, parsed);
    track('tower_search', {
      search_term: q,
      search_source: source || 'submit',
      search_brands: parsed.brands.join('|'),
      search_types: parsed.types.join('|'),
      search_gender: parsed.genders.map((g) => g.label).join('|'),
      search_routed: parsed.understood && C.routing
    });
    location.assign(url.toString());
  };

  // Every search form on the site (header, results page, no-results page)…
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.closest('facets-form') || form.classList.contains('facets')) return; // never hijack filters
      let action;
      try {
        action = new URL(form.getAttribute('action') || location.href, location.href);
      } catch (e) {
        return;
      }
      if (action.pathname !== SEARCH_PATH) return;
      const input = form.elements.q;
      const q = input && input.value ? input.value.trim() : '';
      if (!q) return; // theme already blocks empty searches
      event.preventDefault();
      event.stopImmediatePropagation();
      const liveUrl = (form.id === 'predictive-search-form' || form.closest('[data-tps-scope]')) && typeof liveUrlFor === 'function' ? liveUrlFor(form) : null;
      if (liveUrl) {
        recent.add(q);
        track('tower_search', { search_term: q, search_source: 'header_filtered' });
        location.assign(liveUrl);
        return;
      }
      go(q, form.id === 'predictive-search-form' ? 'header' : 'page');
    },
    true
  );

  // …and every suggestion / trending / recent / style link.
  document.addEventListener('click', (event) => {
    const el = event.target.closest('[data-tps-q]');
    if (!el || event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
    event.preventDefault();
    go(el.getAttribute('data-tps-q'), el.getAttribute('data-tps-source') || 'suggestion');
  });

  // Product / brand / collection clicks inside the panel (for search → click attribution).
  document.addEventListener('click', (event) => {
    const link = event.target.closest('.tps-panel a[href]:not([data-tps-q])');
    if (!link) return;
    const scope = link.closest('header-search, [data-tps-scope]');
    const input = scope && scope.querySelector('.header-search__input, [data-tps-live-input]');
    track('tower_search_click', {
      search_term: input ? input.value.trim() : '',
      click_type: link.getAttribute('data-tps-kind') || 'product',
      click_url: link.getAttribute('href'),
      source: scope && scope.matches('header-search') ? 'panel' : 'panel_page'
    });
  });

  /* ===================================================================== SEARCH → ORDER ATTRIBUTION
     1. Remembers which search led to each product a customer clicks (panel, results page, no-results rescue).
     2. When that product is added to the bag, the line gets hidden properties:
          _search_term   (what they typed)   _search_source (panel / results / filter …)
        and the cart gets hidden attributes _search_session / _search_terms, so every order shows its search.
        Properties starting with "_" are hidden from customers in the bag and checkout, but stay on the order.
     3. Optional: search activity (searches, zero results, clicks, filters, add-to-bags) is sent to the
        Tower search insights service (Theme settings > Tower search > Insights endpoint) for the revenue report.
  ===================================================================================================== */
  const ATTR_KEY = 'tps:attr';
  const ATTR_TTL = 7 * 24 * 3600 * 1000; // a search keeps credit for a product for 7 days
  const handleFromUrl = (href) => {
    try {
      const m = new URL(href, location.origin).pathname.match(/\/products\/([^/?#]+)/);
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) {
      return null;
    }
  };

  const sessionId = (() => {
    let sid = null;
    try {
      sid = window.sessionStorage.getItem('tps:sid');
      if (!sid) {
        sid = `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
        window.sessionStorage.setItem('tps:sid', sid);
      }
    } catch (e) {
      sid = sid || `s${Date.now().toString(36)}`;
    }
    return sid;
  })();

  const attribution = {
    all() {
      const map = store.get(ATTR_KEY, {});
      const now = Date.now();
      Object.keys(map).forEach((k) => now - map[k].ts > ATTR_TTL && delete map[k]);
      return map;
    },
    remember(handle, q, src) {
      if (!handle || !q || q === '*') return;
      const map = attribution.all();
      map[handle] = { q: q.slice(0, 120), src: src || 'search', ts: Date.now() };
      const keys = Object.keys(map);
      if (keys.length > 60) keys.sort((a, b) => map[a].ts - map[b].ts).slice(0, keys.length - 60).forEach((k) => delete map[k]);
      store.set(ATTR_KEY, map);
    },
    for(handle) {
      return handle ? attribution.all()[handle] || null : null;
    },
    // Same-visit credit: the customer moved on from a search result (another colour, a related product…)
    // and adds within 30 minutes → credit the latest search, marked as "_visit".
    recent() {
      const list = Object.values(attribution.all()).sort((a, b) => b.ts - a.ts);
      const last = list[0];
      if (!last || Date.now() - last.ts > 30 * 60 * 1000) return null;
      let sameTab = false;
      try {
        sameTab = window.sessionStorage.getItem('tps:sid') === sessionId;
      } catch (e) {}
      return sameTab ? { q: last.q, src: `${String(last.src || 'search').replace(/_visit$/, '')}_visit` } : null;
    }
  };

  // Insights beacon (fire-and-forget, no personal data: a random per-tab session id, the term, counts).
  const insights = (() => {
    const url = (C.insightsUrl || '').trim();
    const shop = (window.Shopify && window.Shopify.shop) || location.hostname;
    const seen = new Set();
    let panelTimer = 0;
    const send = (type, data) => {
      if (!url) return;
      const body = JSON.stringify(Object.assign({ t: type, sid: sessionId, shop, ts: Date.now() }, data));
      try {
        if (!(navigator.sendBeacon && navigator.sendBeacon(url, body))) {
          fetch(url, { method: 'POST', body, keepalive: true, mode: 'no-cors' }).catch(() => {});
        }
      } catch (e) {}
    };
    const searchOnce = (q, data) => {
      const key = `${(q || '').toLowerCase().trim()}|${data.src}`;
      if (!q || q === '*' || seen.has(key)) return;
      seen.add(key);
      send('search', Object.assign({ q }, data));
    };
    return {
      forward(name, d) {
        if (!url || !d) return;
        switch (name) {
          case 'tower_search': // submitted searches (Enter / suggestion / trending / view all)
            searchOnce(d.search_term, { src: d.search_source || 'submit', b: d.search_brands, ty: d.search_types, g: d.search_gender });
            break;
          case 'tower_search_panel': // live panel: only count a term once typing has settled
            clearTimeout(panelTimer);
            panelTimer = setTimeout(() => searchOnce(d.search_term, { src: 'panel', n: d.results_count, ms: d.ms }), 1500);
            break;
          case 'tower_search_results':
            searchOnce(d.search_term, { src: 'results', n: d.results_count });
            break;
          case 'tower_search_zero_results':
            send('zero', { q: d.search_term });
            break;
          case 'tower_search_panel_filter':
            if (d.active) send('filter', { q: d.search_term, f: d.filter });
            break;
          case 'tower_search_click':
            if (d.click_type === 'product') send('click', { q: d.search_term, h: handleFromUrl(d.click_url), src: d.source });
            break;
          case 'tower_search_add':
            send('add', { q: d.search_term, h: d.handle, src: d.source });
            break;
          default:
        }
      }
    };
  })();

  // Product clicks from search → remember the term for that product
  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest('a[href*="/products/"]');
      if (!link) return;
      const handle = handleFromUrl(link.href);
      let q = null;
      let src = null;
      const scope = link.closest('header-search, [data-tps-scope]');
      if (scope) {
        q = (scope.querySelector('.header-search__input, [data-tps-live-input]') || {}).value;
        src = scope.matches('header-search') ? 'panel' : 'panel_page';
      } else if (location.pathname === SEARCH_PATH && link.closest('.shopify-section--main-search, [data-tps-fallback]')) {
        const p = new URLSearchParams(location.search);
        q = p.get('oq') || p.get('q');
        src = link.closest('[data-tps-fallback]') ? 'rescue' : 'results';
      }
      if (!q || !q.trim()) return;
      attribution.remember(handle, q.trim(), src);
      if (src !== 'panel' && src !== 'panel_page') {
        track('tower_search_click', { search_term: q.trim(), click_type: 'product', click_url: link.getAttribute('href'), source: src });
      }
    },
    true
  );

  // Add to bag → stamp the line with the search that found it
  const setHidden = (form, name, value) => {
    let input = form.querySelector(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.setAttribute('data-tps-attr', '');
      form.appendChild(input);
    }
    input.value = value;
  };

  const handleForForm = (form) => {
    const card = form.closest('product-card, .product-card, .horizontal-product-card, [data-product-handle]');
    const link = card && card.querySelector('a[href*="/products/"]');
    if (link) return handleFromUrl(link.href);
    if (form.dataset.productHandle) return form.dataset.productHandle;
    return handleFromUrl(location.href);
  };

  let cartAttrTimer = 0;
  const updateCartAttributes = () => {
    clearTimeout(cartAttrTimer);
    cartAttrTimer = setTimeout(() => {
      const terms = [...new Set(Object.values(attribution.all()).sort((a, b) => b.ts - a.ts).map((x) => x.q))].slice(0, 5);
      fetch(`${ROOT_URL}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { _search_session: sessionId, _search_terms: terms.join(' | ') } })
      }).catch(() => {});
    }, 400);
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !/\/cart\/add/.test(form.getAttribute('action') || '')) return;
      const handle = handleForForm(form);
      let info = attribution.for(handle);
      if (!info && location.pathname === SEARCH_PATH) {
        const p = new URLSearchParams(location.search);
        const q = p.get('oq') || p.get('q');
        if (q && q !== '*') info = { q, src: 'results_quick_buy' };
      }
      if (!info) info = attribution.recent();
      if (!info) {
        form.querySelectorAll('[data-tps-attr]').forEach((n) => n.remove());
        return;
      }
      setHidden(form, 'properties[_search_term]', info.q);
      setHidden(form, 'properties[_search_source]', info.src);
      track('tower_search_add', { search_term: info.q, handle, source: info.src });
      updateCartAttributes();
    },
    true
  );


  /* ----------------------------------------------------- the search panel */

  const renderRecent = (root) => {
    root.querySelectorAll('[data-tps-recent]').forEach((wrap) => {
      const list = recent.all();
      const block = wrap.closest('[data-tps-recent-block]') || wrap;
      if (!list.length) {
        block.hidden = true;
        return;
      }
      block.hidden = false;
      wrap.innerHTML = list
        .map(
          (q) =>
            `<li><a href="${escapeHtml(C.searchUrl)}?q=${encodeURIComponent(q)}" class="tps-list__link link-reverse" data-tps-q="${escapeHtml(q)}" data-tps-source="recent"><svg class="tps-list__icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg><span>${escapeHtml(q)}</span></a></li>`
        )
        .join('');
    });
  };

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-tps-clear-recent]');
    if (!btn) return;
    event.preventDefault();
    recent.clear();
    renderRecent(document);
  });

  let zeroStatePromise = null;
  const loadZeroState = () => {
    const holders = document.querySelectorAll('[data-tps-zero]');
    if (!holders.length) return;
    if (!zeroStatePromise) {
      zeroStatePromise = fetch(`${C.rootUrl.replace(/\/$/, '')}/?section_id=tower-search-zero-state`, { priority: 'low' })
        .then((r) => (r.ok ? r.text() : ''))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const section = doc.querySelector('.shopify-section');
          return section ? section.innerHTML : '';
        })
        .catch(() => '');
    }
    zeroStatePromise.then((html) => {
      holders.forEach((holder) => {
        if (!holder.dataset.loaded && html) {
          holder.innerHTML = html;
          holder.dataset.loaded = 'true';
          const brands = holder.querySelector('[data-tps-brands]');
          if (brands) {
            try {
              window.TowerSearchBrands = JSON.parse(brands.textContent);
            } catch (e) {}
          }
        }
        renderRecent(holder);
      });
    });
  };

  // Warm up as soon as intent shows: hover/focus on the search icon, or the panel opening.
  document.addEventListener('pointerover', (e) => {
    if (e.target.closest('[aria-controls^="header-search"], .header__search-link')) loadZeroState();
  });
  document.addEventListener('focusin', (e) => {
    if (e.target.closest('.header-search, .header__search-link')) loadZeroState();
  });
  document.querySelectorAll('header-search').forEach((el) => el.addEventListener('dialog:before-show', loadZeroState));

  // Rotating placeholder
  const startPlaceholder = () => {
    const examples = (C.placeholders || []).map((p) => p.trim()).filter(Boolean);
    if (!examples.length) return;
    const inputs = document.querySelectorAll('.header-search__input');
    if (!inputs.length) return;
    const tryWord = (C.i18n && C.i18n.try) || 'Try';
    let i = 0;
    const apply = () => inputs.forEach((input) => (input.placeholder = `${tryWord} “${examples[i % examples.length]}”`));
    apply();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    setInterval(() => {
      if ([...inputs].some((input) => input.value)) return;
      i++;
      apply();
    }, 2600);
  };
  startPlaceholder();

  /* ===================================================================== FAST ENGINE
     Replaces the theme's predictive-search round trip (280ms wait + HTML) with:
     - our own ~90ms debounce, stale requests aborted, every query/filter combination cached;
     - ONE Storefront API call per keystroke returning products + filter counts + suggestions as data
       (falls back to /search/suggest.json + a JSON section when no Storefront token is set);
     - cards drawn in the browser; filters apply in place; picks survive further typing.
  ===================================================================================== */
  const ENGINE = C.engine !== false;
  const isCompact = () => window.matchMedia('(max-width: 999px)').matches;
  // A search "scope" is the header search or any inline live search box (e.g. the one on the results page).
  const INPUT_SEL = '.header-search__input, [data-tps-live-input]';
  const scopeOf = (el) => el && el.closest('header-search, [data-tps-scope]');
  const slotOf = (scope) => scope && scope.querySelector('.predictive-search__content, [data-tps-slot]');
  const panelInput = (el) => scopeOf(el)?.querySelector(INPUT_SEL);
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || C.rootUrl || '/';
  const API_VERSION = '2025-01';
  const PER_PAGE = Math.max(4, Math.min(12, Number(C.panelCount) || 8));
  const PRICE_BANDS = [[0, 50], [50, 100], [100, 150], [150, null]];

  const pairKey = ([k, v]) => `${k}\u0001${v}`;
  const samePair = (a, b) => a[0] === b[0] && a[1] === b[1];
  const handleize = (s) => norm(s).replace(/ /g, '-');

  // --- money -----------------------------------------------------------------------------------
  const moneyFmt = new Map();
  const formatMoney = (amount, currency) => {
    const value = Number(amount);
    if (!currency || Number.isNaN(value)) return '';
    const whole = Math.abs(value % 1) < 0.005;
    const key = `${currency}|${whole}`;
    if (!moneyFmt.has(key)) {
      try {
        moneyFmt.set(
          key,
          new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
            style: 'currency',
            currency,
            minimumFractionDigits: whole ? 0 : 2,
            maximumFractionDigits: 2
          })
        );
      } catch (e) {
        moneyFmt.set(key, { format: (n) => `${currency} ${n}` });
      }
    }
    return moneyFmt.get(key).format(value);
  };

  // --- URL params <-> Storefront ProductFilter inputs --------------------------------------------
  const inputToPairs = (input) => {
    if (!input) return [];
    if (input.productVendor != null) return [['filter.p.vendor', input.productVendor]];
    if (input.productType != null) return [['filter.p.product_type', input.productType]];
    if (input.productMetafield) {
      const m = input.productMetafield;
      return [[`filter.p.m.${m.namespace}.${m.key}`, m.value]];
    }
    if (input.variantMetafield) {
      const m = input.variantMetafield;
      return [[`filter.v.m.${m.namespace}.${m.key}`, m.value]];
    }
    if (input.variantOption) return [[`filter.v.option.${handleize(input.variantOption.name)}`, input.variantOption.value]];
    if (input.available != null) return [['filter.v.availability', input.available ? '1' : '0']];
    if (input.price) {
      const out = [];
      if (input.price.min != null) out.push(['filter.v.price.gte', String(input.price.min)]);
      if (input.price.max != null) out.push(['filter.v.price.lte', String(input.price.max)]);
      return out;
    }
    return [['tps.input', JSON.stringify(input)]]; // taxonomy etc.: works in the panel, not mapped to the URL
  };

  const knownInputs = new Map(); // pairKey -> exact input the API gave us (keeps grouped size values etc. exact)

  const pairsToInputs = (pairs) => {
    const inputs = [];
    let price = null;
    pairs.forEach((p) => {
      const [k, v] = p;
      if (knownInputs.has(pairKey(p))) return inputs.push(knownInputs.get(pairKey(p)));
      if (k === 'tps.input') return inputs.push(JSON.parse(v));
      if (k === 'filter.p.vendor') return inputs.push({ productVendor: v });
      if (k === 'filter.p.product_type') return inputs.push({ productType: v });
      if (k === 'filter.v.availability') return inputs.push({ available: v === '1' });
      if (k === 'filter.v.price.gte' || k === 'filter.v.price.lte') {
        price = price || {};
        price[k.endsWith('gte') ? 'min' : 'max'] = Number(v);
        return;
      }
      let m = k.match(/^filter\.p\.m\.([^.]+)\.(.+)$/);
      if (m) return inputs.push({ productMetafield: { namespace: m[1], key: m[2], value: v } });
      m = k.match(/^filter\.v\.m\.([^.]+)\.(.+)$/);
      if (m) return inputs.push({ variantMetafield: { namespace: m[1], key: m[2], value: v } });
      m = k.match(/^filter\.v\.option\.(.+)$/);
      if (m) return inputs.push({ variantOption: { name: m[1], value: v } });
    });
    if (price) inputs.push({ price });
    return inputs;
  };

  // --- state -------------------------------------------------------------------------------------
  // Filters shown = routed filters for the query (brand / style / gender) + customer picks - customer removals.
  const S = { add: [], remove: [], timer: 0, abort: null, seq: 0, cache: new Map(), last: null };
  const hasPicks = () => S.add.length > 0 || S.remove.length > 0;

  const routedFor = (q) => {
    const parsed = parse(q);
    const url = buildUrl(q, parsed);
    url.searchParams.set('type', 'product');
    return { parsed, url, pairs: [...url.searchParams].filter(([k]) => k.startsWith('filter.')) };
  };

  const statePairs = (routedPairs) => {
    const out = routedPairs.filter((p) => !S.remove.some((r) => samePair(r, p)));
    S.add.forEach((p) => {
      if (p[0].startsWith('filter.v.price.')) {
        for (let i = out.length - 1; i >= 0; i--) if (out[i][0] === p[0]) out.splice(i, 1);
      }
      if (!out.some((o) => samePair(o, p))) out.push(p);
    });
    return out;
  };

  const resultsUrl = (routed, pairs) => {
    const u = new URL(routed.url);
    [...u.searchParams.keys()].filter((k) => k.startsWith('filter.')).forEach((k) => u.searchParams.delete(k));
    pairs.filter(([k]) => k.startsWith('filter.')).forEach(([k, v]) => u.searchParams.append(k, v));
    return u.pathname + u.search;
  };

  // --- providers -------------------------------------------------------------------------------
  const GQL = `query TowerSearch($q: String!, $pq: String!, $filters: [ProductFilter!], $first: Int!, $country: CountryCode, $language: LanguageCode) @inContext(country: $country, language: $language) {
  search(query: $q, first: $first, types: [PRODUCT], productFilters: $filters, prefix: LAST, unavailableProducts: LAST) {
    totalCount
    productFilters { id label type values { id label count input } }
    nodes { ... on Product { ...P } }
  }
  predictiveSearch(query: $pq, limit: 6, limitScope: EACH, types: [QUERY, COLLECTION, PRODUCT]) {
    queries { text styledText }
    collections { title handle }
    products { ...P }
  }
}
fragment P on Product {
  title handle vendor productType availableForSale
  priceRange { minVariantPrice { amount currencyCode } }
  compareAtPriceRange { maxVariantPrice { amount currencyCode } }
  featuredImage { url(transform: { maxWidth: 400 }) altText width height }
}`;

  const localeCode = () => {
    const l = ((window.Shopify && window.Shopify.locale) || document.documentElement.lang || 'en').toUpperCase().replace('-', '_');
    return /^[A-Z]{2}(_[A-Z]{2})?$/.test(l) ? l : 'EN';
  };

  const mapProduct = (p) => ({
    title: p.title,
    url: `${ROOT.replace(/\/$/, '')}/products/${p.handle}`,
    vendor: p.vendor,
    image: p.featuredImage && p.featuredImage.url,
    alt: (p.featuredImage && p.featuredImage.altText) || p.title,
    price: p.priceRange.minVariantPrice.amount,
    compare: p.compareAtPriceRange && p.compareAtPriceRange.maxVariantPrice.amount,
    currency: p.priceRange.minVariantPrice.currencyCode,
    available: p.availableForSale
  });

  const storefrontProvider = async ({ q, pq, inputs, signal }) => {
    // The Storefront API always lives at the domain root, even on /en-ie/-style market sub-paths.
    const res = await fetch(`/api/${API_VERSION}/graphql.json`, {
      method: 'POST',
      signal,
      headers: C.storefrontToken
        ? { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': C.storefrontToken }
        : { 'Content-Type': 'application/json' }, // tokenless access: no Headless channel needed
      body: JSON.stringify({
        query: GQL,
        variables: {
          q,
          pq,
          filters: inputs,
          first: PER_PAGE,
          country: (window.Shopify && window.Shopify.country) || undefined,
          language: localeCode()
        }
      })
    });
    const json = await res.json();
    if (!json.data) throw new Error((json.errors && json.errors[0] && json.errors[0].message) || 'Storefront error');
    const { search, predictiveSearch: ps } = json.data;
    return {
      total: search.totalCount,
      products: search.nodes.filter((n) => n && n.handle).map(mapProduct),
      fuzzyProducts: ((ps && ps.products) || []).map(mapProduct),
      queries: ((ps && ps.queries) || []).map((x) => ({ text: x.text, html: x.styledText })),
      collections: ((ps && ps.collections) || []).map((c) => ({ title: c.title, url: `${ROOT.replace(/\/$/, '')}/collections/${c.handle}` })),
      filters: (search.productFilters || []).map((f) => ({
        label: f.label,
        type: f.type === 'PRICE_RANGE' ? 'price' : f.type === 'BOOLEAN' ? 'boolean' : 'list',
        max: f.type === 'PRICE_RANGE' ? (() => { try { return JSON.parse(f.values[0].input).price.max; } catch (e) { return null; } })() : null,
        values: f.values.map((v) => {
          let input = null;
          try { input = JSON.parse(v.input); } catch (e) {}
          const pairs = inputToPairs(input);
          pairs.forEach((p) => input && knownInputs.set(pairKey(p), input));
          return { label: v.label, count: v.count, pairs };
        })
      }))
    };
  };

  // No token: products/suggestions from /search/suggest.json (fast), counts + filters from a JSON section.
  const fallbackProvider = async ({ pq, url, signal, needProducts }) => {
    const suggestUrl = `${C.searchUrl}/suggest.json?q=${encodeURIComponent(pq)}&resources[type]=product,query,collection&resources[limit]=${PER_PAGE}&resources[limit_scope]=each`;
    const sectionUrl = new URL(url, location.origin);
    sectionUrl.searchParams.set('section_id', 'tower-search-panel-filters');
    const [suggest, section] = await Promise.all([
      fetch(suggestUrl, { signal }).then((r) => r.json()).catch(() => null),
      fetch(sectionUrl.toString(), { signal })
        .then((r) => r.text())
        .then((html) => {
          const el = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-tps-json]');
          return el ? JSON.parse(el.textContent) : null;
        })
        .catch(() => null)
    ]);
    const r = (suggest && suggest.resources && suggest.resources.results) || {};
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || (section && section.currency) || 'GBP';
    const fromSuggest = (r.products || []).map((p) => ({
      title: p.title,
      url: p.url.split('?')[0],
      vendor: p.vendor,
      image: p.featured_image && p.featured_image.url ? `${p.featured_image.url}${p.featured_image.url.includes('?') ? '&' : '?'}width=400` : p.image,
      alt: (p.featured_image && p.featured_image.alt) || p.title,
      price: p.price,
      compare: p.compare_at_price_max,
      currency,
      available: p.available
    }));
    const sectionProducts = ((section && section.products) || []).map((p) => ({ ...p, price: p.price / 100, compare: p.compare / 100, currency: section.currency }));
    return {
      total: section ? section.total : fromSuggest.length,
      products: needProducts ? sectionProducts : fromSuggest,
      fuzzyProducts: fromSuggest,
      queries: (r.queries || []).map((x) => ({ text: x.text, html: x.styled_text })),
      collections: (r.collections || []).map((c) => ({ title: c.title, url: c.url })),
      filters: ((section && section.filters) || []).map((f) => ({
        label: f.label,
        type: f.type === 'price_range' ? 'price' : f.type === 'boolean' ? 'boolean' : 'list',
        max: f.max != null ? f.max / 100 : null,
        values: (f.values || []).map((v) => ({ label: v.label, count: v.count, pairs: [[v.param, v.value]] }))
      }))
    };
  };

  // Provider choice: token → Storefront API. No token → try Shopify's tokenless Storefront access once per session;
  // if the store doesn't allow it (or it can't filter), drop to the fallback for the rest of the session.
  // Provider choice: the fast Storefront route (tokenless, or with a token) is always preferred.
  // A failed request falls back for THAT search only. Only 3 failures in a row (e.g. the store blocks tokenless
  // access) switch to the fallback, and then only for 5 minutes, after which the fast route is retried.
  const FB_KEY = 'tps:fallback-until';
  const fallbackUntil = () => {
    try {
      return Number(window.sessionStorage.getItem(FB_KEY) || 0);
    } catch (e) {
      return 0;
    }
  };
  let sfMode = fallbackUntil() > Date.now() ? 'fb' : 'sf';
  let sfFailures = 0;
  const usingSF = () => sfMode !== 'fb' || fallbackUntil() <= Date.now();
  try {
    window.sessionStorage.removeItem('tps:tokenless'); // old v2.x "stuck in fallback" flag
  } catch (e) {}
  const provider = async (args) => {
    if (!usingSF()) return fallbackProvider(args);
    sfMode = 'sf';
    try {
      const data = await storefrontProvider(args);
      sfFailures = 0;
      return data;
    } catch (e) {
      if (e && e.name === 'AbortError') throw e;
      sfFailures++;
      if (sfFailures >= 3) {
        sfMode = 'fb';
        sfFailures = 0;
        try {
          window.sessionStorage.setItem(FB_KEY, String(Date.now() + 5 * 60 * 1000));
        } catch (err) {}
      }
      if (!S.warned) {
        S.warned = true;
        console.warn('[Tower search] Fast search request failed, using the fallback for this search.', e);
      }
      return fallbackProvider(args);
    }
  };

  // --- rendering -------------------------------------------------------------------------------
  const t = (key, fallback) => (C.i18n && C.i18n[key]) || fallback;

  const cardHtml = (p, i) => {
    const sale = p.compare && Number(p.compare) > Number(p.price);
    const img = p.image
      ? `<img src="${escapeHtml(p.image)}" alt="${escapeHtml(p.alt || '')}" loading="${i < 4 ? 'eager' : 'lazy'}" decoding="async" width="400" height="400">`
      : '';
    return `<div class="tps-grid__item"><a href="${escapeHtml(p.url)}" class="tps-card" data-tps-kind="product">
      <span class="tps-card__media">${img}</span>
      <span class="tps-card__info">
        <span class="tps-card__vendor">${escapeHtml(p.vendor || '')}</span>
        <span class="tps-card__title">${escapeHtml(p.title)}</span>
        <span class="tps-card__price">${sale ? `<span class="tps-card__sale">${formatMoney(p.price, p.currency)}</span> <s>${formatMoney(p.compare, p.currency)}</s>` : formatMoney(p.price, p.currency)}</span>
      </span></a></div>`;
  };

  const brandLinks = (q, parsed) => {
    const nq = norm(q);
    const found = [...parsed.brands];
    if (nq.length >= 2) {
      vendorsByNorm.forEach((vs, n) => {
        if (found.length < 4 && n.startsWith(nq)) vs.forEach((v) => !found.includes(v) && found.push(v));
      });
    }
    const map = (window.TowerSearchBrands = window.TowerSearchBrands || {});
    return found.slice(0, 4).map((v) => {
      const c = map[v];
      const href = c ? c.url : `${C.searchUrl}?q=${encodeURIComponent(v)}&type=product&filter.p.vendor=${encodeURIComponent(v)}`;
      const meta = c && c.count ? `<span class="tps-list__meta text-subdued">${escapeHtml(t('stylesCount', '[count] styles').replace('[count]', c.count))}</span>` : '';
      return `<li><a href="${escapeHtml(href)}" class="tps-list__link tps-list__link--brand link-reverse" data-tps-kind="brand"><span class="tps-brand-name">${escapeHtml(v)}</span>${meta}</a></li>`;
    });
  };

  const filtersHtml = (filters, active) => {
    const isActive = (pairs) => pairs.length > 0 && pairs.every((p) => active.some((a) => samePair(a, p)));
    const groups = [];
    let shown = 0;
    filters.forEach((f) => {
      if (f.type === 'boolean') return;
      // Availability (In stock / Out of stock) is never shown in the panel
      if (/availab/i.test(f.label) || f.values.some((v) => v.pairs.some(([k]) => k === 'filter.v.availability'))) return;
      let chips = '';
      let activeCount = 0;
      if (f.type === 'price') {
        const max = f.max || 0;
        PRICE_BANDS.forEach(([lo, hi]) => {
          if (max && lo >= max) return;
          const pairs = [['filter.v.price.gte', String(lo)]];
          if (hi != null) pairs.push(['filter.v.price.lte', String(hi)]);
          const on = active.some((a) => a[0] === 'filter.v.price.gte' && a[1] === String(lo)) &&
            (hi == null ? !active.some((a) => a[0] === 'filter.v.price.lte') : active.some((a) => samePair(a, ['filter.v.price.lte', String(hi)])));
          if (on) activeCount++;
          const cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || C.currency || 'GBP';
          const label = lo === 0
            ? t('priceUnder', 'Under [price]').replace('[price]', formatMoney(hi, cur))
            : hi == null ? `${formatMoney(lo, cur)}+` : `${formatMoney(lo, cur)}–${formatMoney(hi, cur)}`;
          chips += `<button type="button" class="tps-facet ${on ? 'is-active' : ''}" aria-pressed="${on}" data-tps-pairs='${escapeHtml(JSON.stringify(pairs))}' data-tps-price>${escapeHtml(label)}</button>`;
        });
      } else {
        const values = f.values.filter((v) => v.count > 0 || isActive(v.pairs));
        if (values.length < 2 && !values.some((v) => isActive(v.pairs))) return;
        const isSize = /size/i.test(f.label);
        values.forEach((v, i) => {
          const on = isActive(v.pairs);
          if (on) activeCount++;
          chips += `<button type="button" class="tps-facet ${on ? 'is-active' : ''} ${i >= 8 ? 'tps-facet--more' : ''}" aria-pressed="${on}" data-tps-pairs='${escapeHtml(JSON.stringify(v.pairs))}'>${escapeHtml(v.label.replace(/\|/g, ' & '))} <span class="tps-facet__count">(${v.count})</span></button>`;
        });
        if (values.length > 8) chips += `<button type="button" class="tps-filter__more link text-xs" data-tps-filter-more>${escapeHtml(t('showMore', 'Show more'))}</button>`;
      }
      if (!chips) return;
      const open = activeCount > 0 || (!isCompact() && shown < 3);
      groups.push(`<details class="tps-filter ${/size/i.test(f.label) ? 'tps-filter--size' : ''}" ${open ? 'open' : ''} data-tps-filter="${escapeHtml(f.label)}">
        <summary class="tps-filter__title h6">${escapeHtml(f.label)}${activeCount ? ` <span class="tps-filter__active">${activeCount}</span>` : ''}</summary>
        <div class="tps-facets">${chips}</div></details>`);
      shown++;
    });
    return groups.join('');
  };

  const openState = new Map(); // remember which filter groups the customer opened/closed

  const render = (slot, q, data, routed, pairs) => {
    const usingFuzzy = data.total === 0 && !hasPicks() && data.fuzzyProducts.length > 0;
    const products = usingFuzzy ? data.fuzzyProducts : data.products;
    const total = usingFuzzy ? data.fuzzyProducts.length : data.total;
    const url = resultsUrl(routed, pairs);
    S.last = { url, total };

    if (!products.length && !data.queries.length && !data.collections.length && !hasPicks()) {
      slot.innerHTML = `<div class="tps-panel tps-no-results"><p class="predictive-search__no-results text-lg">${escapeHtml(t('noResults', 'No results for “[terms]”').replace('[terms]', q))}</p></div>`;
      return;
    }

    const queries = data.queries.filter((x) => norm(x.text) !== norm(q)).slice(0, 5);
    const brands = brandLinks(q, routed.parsed);
    const filters = filtersHtml(data.filters, pairs);
    const genderWord = routed.parsed.genders.length > 0;
    const genderChips = !genderWord && C.genderChips
      ? C.genderChips.filter(Boolean).map((g) => `<a href="${C.searchUrl}?q=${encodeURIComponent(`${q} ${g}`)}" class="tps-chip tps-chip--sm" data-tps-q="${escapeHtml(`${q} ${g}`)}" data-tps-source="gender">${escapeHtml(g)}</a>`).join('')
      : '';

    slot.innerHTML = `<div class="tps-panel tps-results ${filters || hasPicks() ? 'has-filters' : ''}">
      <div class="tps-results__aside">
        ${queries.length ? `<div class="tps-group tps-group--suggestions"><p class="tps-group__title h6 text-subdued">${escapeHtml(t('suggestions', 'Suggestions'))}</p><ul class="tps-list unstyled-list" role="list">${queries
          .map((x) => `<li><a href="${C.searchUrl}?q=${encodeURIComponent(x.text)}" class="tps-list__link link-reverse" data-tps-q="${escapeHtml(x.text)}" data-tps-source="suggestion"><span>${x.html || escapeHtml(x.text)}</span></a></li>`)
          .join('')}</ul></div>` : ''}
        ${brands.length ? `<div class="tps-group tps-group--brands"><p class="tps-group__title h6 text-subdued">${escapeHtml(t('brands', 'Brands'))}</p><ul class="tps-list unstyled-list" role="list">${brands.join('')}</ul></div>` : ''}
        ${filters || hasPicks() ? `<div class="tps-group tps-group--filters"><div class="tps-group__header"><p class="tps-group__title h6 text-subdued">${escapeHtml(t('filters', 'Filter'))}</p>${hasPicks() ? `<button type="button" class="tps-group__action link-faded" data-tps-live-clear>${escapeHtml(t('clearFilters', 'Clear filters'))}</button>` : ''}</div><div class="tps-filters">${filters}</div></div>` : ''}
        ${data.collections.length ? `<div class="tps-group tps-group--collections"><p class="tps-group__title h6 text-subdued">${escapeHtml(t('collections', 'Collections'))}</p><ul class="tps-list unstyled-list" role="list">${data.collections
          .slice(0, 4)
          .map((c) => `<li><a href="${escapeHtml(c.url)}" class="tps-list__link link-reverse" data-tps-kind="collection">${escapeHtml(c.title)}</a></li>`)
          .join('')}</ul></div>` : ''}
      </div>
      <div class="tps-results__main">
        <div class="tps-results__head">
          <p class="tps-group__title h6 text-subdued">${escapeHtml(t('products', 'Products'))}${total ? ` <span class="tps-results__total">${Number(total).toLocaleString()}</span>` : ''}</p>
          ${genderChips ? `<div class="tps-gender-links"><span class="text-subdued">${escapeHtml(t('narrowGender', 'Show me'))}</span>${genderChips}</div>` : ''}
          ${total ? `<a href="${escapeHtml(url)}" class="button button--sm tps-results__top-cta" data-tps-view-all>${escapeHtml(t('viewAllCount', 'View all [count] results').replace('[count]', Number(total).toLocaleString()))}</a>` : ''}
        </div>
        <div class="tps-grid">${products.length ? products.map(cardHtml).join('') : `<p class="tps-live-empty text-subdued">${escapeHtml(t('noMatches', 'No products match these filters.'))}</p>`}</div>
        ${total ? `<div class="tps-results__cta"><a href="${escapeHtml(url)}" class="button w-full" data-tps-view-all>${escapeHtml(t('viewAllCount', 'View all [count] results').replace('[count]', Number(total).toLocaleString()))}</a></div>` : ''}
      </div>
    </div>`;

    slot.querySelectorAll('details[data-tps-filter]').forEach((d) => {
      const k = d.getAttribute('data-tps-filter');
      if (openState.has(k)) d.open = openState.get(k);
      d.addEventListener('toggle', () => openState.set(k, d.open));
    });
  };

  const skeleton = () =>
    `<div class="tps-skeleton" aria-hidden="true">${'<div class="tps-skeleton__item"><div class="tps-skeleton__img"></div><div class="tps-skeleton__text"><div class="tps-skeleton__line"></div><div class="tps-skeleton__line"></div></div></div>'.repeat(4)}</div>`;

  // --- the loop --------------------------------------------------------------------------------
  const run = (input, { immediate = false } = {}) => {
    const scope = scopeOf(input);
    const slot = slotOf(scope);
    if (!slot) return;
    const q = input.value.trim();
    clearTimeout(S.timer);

    if (!q) {
      if (S.abort) S.abort.abort();
      S.add = [];
      S.remove = [];
      S.last = null;
      slot.innerHTML = '';
      scope.classList.remove('tps-has-results');
      return;
    }

    const routed = routedFor(q);
    const pairs = statePairs(routed.pairs);
    let searchQ = routed.url.searchParams.get('q') || q;
    // Storefront search needs words: for "mens trainers" (q = *) search the style name, filtered by type + gender.
    if (searchQ === '*' && usingSF() && routed.parsed.types.length) searchQ = routed.parsed.types[0];
    const needProducts = !usingSF() && hasPicks();
    const key = JSON.stringify([usingSF() ? 'sf' : 'fb', searchQ, q, pairs, needProducts]);
    const seq = ++S.seq;

    const show = (data) => {
      if (seq !== S.seq || input.value.trim() !== q) return;
      scope.classList.remove('tps-busy');
      render(slot, q, data, routed, pairs);
      scope.classList.add('tps-has-results');
    };

    if (S.cache.has(key)) {
      const cached = S.cache.get(key);
      if (cached.data) return show(cached.data);
    }
    if (!slot.children.length) slot.innerHTML = skeleton();
    else scope.classList.add('tps-busy');
    scope.classList.add('tps-has-results');

    S.timer = setTimeout(() => {
      if (S.abort) S.abort.abort();
      const controller = new AbortController();
      S.abort = controller;
      const started = performance.now();
      const entry = S.cache.get(key) || {
        promise: provider({ q: searchQ, pq: q, inputs: pairsToInputs(pairs), url: resultsUrl(routed, pairs), signal: controller.signal, needProducts })
          .then(async (data) => {
            // "*" can't be text-searched by every endpoint: retry once with the words the customer typed.
            if (data.total === 0 && searchQ === '*' && usingSF()) {
              const retry = await provider({ q, pq: q, inputs: pairsToInputs(pairs), signal: controller.signal });
              return retry;
            }
            return data;
          })
      };
      S.cache.set(key, entry);
      entry.promise
        .then((data) => {
          entry.data = data;
          track('tower_search_panel', { search_term: q, results_count: data.total, ms: Math.round(performance.now() - started), engine: sfMode === 'fb' ? 'fallback' : C.storefrontToken ? 'storefront' : 'tokenless' });
          show(data);
        })
        .catch((e) => {
          S.cache.delete(key);
          if (e && e.name === 'AbortError') return;
          scope.classList.remove('tps-busy');
        });
    }, immediate ? 0 : C.debounce || 90);
  };

  // Prefetch: warm the cache for trending searches the first time the search opens.
  let prefetched = false;
  const prefetch = () => {
    if (prefetched || !ENGINE) return;
    prefetched = true;
    (C.prefetch || []).filter(Boolean).slice(0, 4).forEach((q, i) => {
      setTimeout(() => {
        const routed = routedFor(q);
        const pairs = routed.pairs;
        const searchQ = routed.url.searchParams.get('q') || q;
        const key = JSON.stringify([usingSF() ? 'sf' : 'fb', searchQ, q, pairs, false]);
        if (S.cache.has(key)) return;
        const entry = { promise: provider({ q: searchQ, pq: q, inputs: pairsToInputs(pairs), url: resultsUrl(routed, pairs), needProducts: false }) };
        S.cache.set(key, entry);
        entry.promise.then((d) => (entry.data = d)).catch(() => S.cache.delete(key));
      }, 300 * i);
    });
  };

  if (ENGINE) {
    document.documentElement.classList.add('tps-engine');

    // Take over typing from the theme's predictive search (capture phase runs before the theme's listener).
    document.addEventListener(
      'input',
      (event) => {
        const input = event.target.closest && event.target.closest(INPUT_SEL);
        if (!input) return;
        event.stopImmediatePropagation();
        run(input);
        const inline = input.closest('[data-tps-scope]');
        if (inline) {
          inline.classList.toggle('is-open', !!input.value.trim());
          input.setAttribute('aria-expanded', String(inline.classList.contains('is-open')));
        }
      },
      true
    );

    document.addEventListener('pointerover', (e) => {
      if (e.target.closest('[aria-controls^="header-search"], .header__search-link')) prefetch();
    });
    document.addEventListener('focusin', (e) => {
      if (e.target.closest('.header-search')) prefetch();
    });

    // Tap a filter: apply in place, instantly from cache when possible.
    document.addEventListener('click', (event) => {
      const more = event.target.closest('[data-tps-filter-more]');
      if (more) {
        event.preventDefault();
        more.parentElement.classList.add('is-expanded');
        more.remove();
        return;
      }

      const chip = event.target.closest('.tps-panel [data-tps-pairs]');
      const clear = event.target.closest('.tps-panel [data-tps-live-clear]');
      if (!chip && !clear) return;
      event.preventDefault();
      const input = panelInput(chip || clear);
      if (!input) return;

      if (clear) {
        S.add = [];
        S.remove = [];
      } else {
        const pairs = JSON.parse(chip.getAttribute('data-tps-pairs'));
        const on = chip.getAttribute('aria-pressed') === 'true';
        const routedPairs = routedFor(input.value.trim()).pairs;
        if (chip.hasAttribute('data-tps-price')) {
          S.add = S.add.filter(([k]) => !k.startsWith('filter.v.price.'));
          if (!on) S.add.push(...pairs);
        } else {
          pairs.forEach((p) => {
            const routedHas = routedPairs.some((r) => samePair(r, p));
            if (on) {
              S.add = S.add.filter((a) => !samePair(a, p));
              if (routedHas && !S.remove.some((r) => samePair(r, p))) S.remove.push(p);
            } else {
              S.remove = S.remove.filter((r) => !samePair(r, p));
              if (!routedHas && !S.add.some((a) => samePair(a, p))) S.add.push(p);
            }
          });
        }
        const group = chip.closest('[data-tps-filter]');
        const value = (chip.firstChild && chip.firstChild.textContent ? chip.firstChild.textContent : chip.textContent).trim();
        track('tower_search_panel_filter', { search_term: input.value.trim(), filter: group ? `${group.getAttribute('data-tps-filter')}: ${value}` : value, active: !on });
      }
      run(input, { immediate: true });
    });

    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-tps-view-all]');
      if (!link) return;
      const input = panelInput(link);
      if (input && input.value.trim()) recent.add(input.value.trim());
      track('tower_search', { search_term: input ? input.value.trim() : '', search_source: 'panel_view_all' });
    });
  }

  // Inline live search boxes (results page, no-results page): open on typing/focus, close on outside click / Esc.
  if (ENGINE) {
    const inlineScopes = () => document.querySelectorAll('[data-tps-scope]');
    document.addEventListener('focusin', (e) => {
      const scope = e.target.closest('[data-tps-scope]');
      if (scope && slotOf(scope).children.length) scope.classList.add('is-open');
    });
    document.addEventListener('pointerdown', (e) => {
      inlineScopes().forEach((scope) => {
        if (!scope.contains(e.target)) scope.classList.remove('is-open');
      });
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const scope = e.target.closest && e.target.closest('[data-tps-scope]');
      if (scope) {
        scope.classList.remove('is-open');
        scope.querySelector('[data-tps-live-input]')?.focus();
      }
    });
  }

  // "View all" / Enter keeps exactly what the panel shows.
  const liveUrlFor = () => (ENGINE && S.last && hasPicks() ? S.last.url : null);


  // Keyboard navigation: ↓/↑ move through every result / chip in the panel.
  const navItems = (panel) =>
    [...panel.querySelectorAll('.tps-panel a[href], .tps-panel button:not([disabled]), .predictive-search a[href], .predictive-search button[type="submit"]')].filter(
      (el) => el.offsetParent !== null && !el.closest('[hidden]')
    );

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const panel = event.target.closest('header-search, [data-tps-scope]');
    if (!panel) return;
    const items = navItems(panel);
    if (!items.length) return;
    const input = panel.querySelector('.header-search__input, [data-tps-live-input]');
    const idx = items.indexOf(document.activeElement);
    event.preventDefault();
    if (event.key === 'ArrowDown') {
      (items[idx + 1] || items[0]).focus();
    } else if (idx <= 0) {
      input && input.focus();
    } else {
      items[idx - 1].focus();
    }
  });

  /* --------------------------------------------------- the results page */

  const onSearchPage = location.pathname === SEARCH_PATH;

  // Gender quick chips (one tap = Mens incl. unisex, etc.)
  document.addEventListener('click', (event) => {
    const chip = event.target.closest('[data-tps-gender-chip]');
    if (!chip) return;
    event.preventDefault();
    const values = JSON.parse(chip.getAttribute('data-values') || '[]');
    const active = chip.getAttribute('aria-pressed') === 'true';
    const url = new URL(location.href);
    url.searchParams.delete(C.genderParam);
    url.searchParams.delete('page');
    url.searchParams.delete('oq');
    if (!active) values.forEach((v) => url.searchParams.append(C.genderParam, v));
    track('tower_search_quick_filter', { filter: 'gender', value: chip.textContent.trim(), active: !active });
    const sectionId = sectionIdOf(chip);
    if (sectionId) {
      url.searchParams.set('section_id', sectionId);
      chip.dispatchEvent(new CustomEvent('facet:update', { bubbles: true, detail: { url } }));
    } else {
      location.assign(url.toString());
    }
  });

  // Load more
  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('[data-tps-load-more]');
    if (!btn || btn.getAttribute('aria-busy') === 'true') return;
    event.preventDefault();
    const nextUrl = btn.getAttribute('data-next-url');
    const sectionId = sectionIdOf(btn);
    const wrap = btn.closest('[data-tps-pager]');
    const list = wrap && wrap.parentElement.querySelector('product-list');
    if (!nextUrl || !sectionId || !list) {
      if (nextUrl) location.assign(nextUrl);
      return;
    }
    btn.setAttribute('aria-busy', 'true');
    const label = btn.querySelector('[data-label]');
    const original = label ? label.textContent : '';
    if (label) label.textContent = (C.i18n && C.i18n.loading) || 'Loading…';
    try {
      const url = new URL(nextUrl, location.origin);
      url.searchParams.set('section_id', sectionId);
      const html = await (await fetch(url.toString())).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const cards = doc.querySelectorAll('product-list > *');
      const firstNew = cards[0];
      cards.forEach((card) => {
        card.querySelectorAll('[reveal-on-scroll]').forEach((n) => n.removeAttribute('reveal-on-scroll'));
        card.removeAttribute('reveal-on-scroll');
        list.appendChild(document.importNode(card, true));
      });
      const newPager = doc.querySelector('[data-tps-pager]');
      if (newPager) wrap.replaceWith(document.importNode(newPager, true));
      else wrap.remove();
      if (firstNew) {
        const link = list.querySelector(`[href="${firstNew.querySelector('a[href]')?.getAttribute('href')}"]`);
        link && link.focus({ preventScroll: true });
      }
      track('tower_search_load_more', { page: url.searchParams.get('page') });
    } catch (e) {
      location.assign(nextUrl);
    } finally {
      btn.removeAttribute('aria-busy');
      if (label) label.textContent = original;
    }
  });

  // QA helper in the browser console: TowerSearch.parse('docs mens chelsea')
  window.TowerSearch = { version: '2.5.2', parse, buildUrl: (q) => buildUrl(q, parse(q)).toString(), engine: () => (typeof usingSF === 'function' ? (usingSF() ? 'fast' : 'fallback') : 'theme') };

  if (!onSearchPage) return;

  const params = new URLSearchParams(location.search);
  const originalQuery = params.get('oq');
  const terms = params.get('q') || '';

  // "Showing Dr. Martens · Search everything for 'docs' instead"
  const note = document.querySelector('[data-tps-routed-note]');
  if (originalQuery) {
    document.querySelectorAll('[data-tps-page-input]').forEach((input) => (input.value = originalQuery));
  }
  if (note && originalQuery) {
    const raw = `${C.searchUrl}?q=${encodeURIComponent(originalQuery)}`;
    note.querySelector('[data-tps-raw-link]')?.setAttribute('href', raw);
    note.querySelectorAll('[data-tps-original]').forEach((n) => (n.textContent = originalQuery));
    note.hidden = false;
  }

  // Zero results: pull typo-tolerant "closest matches" from predictive search.
  const fallback = document.querySelector('[data-tps-fallback]');
  const fallbackQuery = originalQuery || terms;
  if (fallback && fallbackQuery && fallbackQuery !== '*') {
    track('tower_search_zero_results', { search_term: fallbackQuery });
    const url = `${C.searchUrl}/suggest?q=${encodeURIComponent(fallbackQuery)}&section_id=tower-search-fallback&resources[type]=product,query,collection&resources[limit]=8&resources[limit_scope]=each`;
    fetch(url)
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const section = doc.querySelector('.shopify-section');
        if (section && section.textContent.trim()) {
          fallback.innerHTML = section.innerHTML;
          fallback.hidden = false;
        }
      })
      .catch(() => {});

    // If the query had a brand, offer "all <brand>" as an instant way out.
    const parsed = parse(fallbackQuery);
    const out = document.querySelector('[data-tps-brand-escape]');
    if (out && parsed.brands.length === 1) {
      const brand = parsed.brands[0];
      const u = new URL(C.searchUrl, location.origin);
      u.searchParams.set('q', brand);
      u.searchParams.set('type', 'product');
      u.searchParams.append('filter.p.vendor', brand);
      out.innerHTML = `<a class="tps-chip tps-chip--accent" href="${u.pathname}${u.search}">${escapeHtml(out.getAttribute('data-label').replace('[brand]', brand))}</a>`;
      out.hidden = false;
    }
  } else if (terms) {
    const countEl = document.querySelector('[data-tps-count]');
    track('tower_search_results', {
      search_term: originalQuery || terms,
      results_count: countEl ? Number(countEl.getAttribute('data-tps-count')) : null
    });
  }
})();
